import { Container, Graphics, Text, type TextStyleOptions } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { ABILITY_DEFS } from '../sim/abilities/AbilityDefs';
import { CHARACTER_LIST, isCharacterUnlocked } from '../sim/characters/CharacterDefs';
import { ACTIVE_DEFS } from '../sim/characters/actives';
import {
  bestTime, maxUnlockedTier, tierDamageMult, tierHpMult, tierLabel, tierQualityFloor,
} from '../sim/run/tiers';
import { formatRunTime } from './EndScene';
import { fitScene } from '../ui/fitLayout';
import { FocusList } from '../ui/FocusList';
import { RelicScene } from './RelicScene';

/** Card grid (2026-07-28, Sarah's ask): full rectangle per character with
 *  name + description on the card (picture slot reserved). 5+4 layout —
 *  a 3×3 grid plus the detail strip busts the MIN_H 560 budget. */
const CARD_W = 172;
const CARD_H = 130;
const CARD_GAP = 10;
const GRID_COLS = 5;
const PANEL_W = GRID_COLS * CARD_W + (GRID_COLS - 1) * CARD_GAP; // 900
const PANEL_H = 68; // compact detail strip — name/desc live on the cards now
const MIN_W = PANEL_W + 40;
/** Layout budget: scale stays 1.0 on 1280×800 AND 1280×720-with-chrome. */
const MIN_H = 560;

/** Vertical stops: character cards → tier ladder → CONTINUE. */
type Row = 0 | 1 | 2;

/**
 * Wizard step 1 (Deck menu split): pick who descends and at what tier, with
 * a detail panel roomy enough for the aspect to actually be read. CONTINUE
 * pushes the relic step; cancel returns to the menu.
 *
 * Layout is rebuilt whenever the screen size changes (syncLayout) — menus
 * built once in constructors were the Deck cut-off bug.
 */
export class CharacterScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private row: Row = 0;
  private readonly chars = new FocusList(CHARACTER_LIST.length);
  private acceptDelay = 8;
  private dynamic = new Container();
  private dirty = true;
  private lastDevice: 'kb' | 'pad' = 'kb';

  private vw = 0;
  private vh = 0;
  private lastW = 0;
  private lastH = 0;
  private gridY = 0;
  private panelY = 0;
  private tierY = 0;
  private continueY = 0;

  constructor(
    private readonly ctx: GameContext,
    private readonly seed: string,
  ) {
    this.root.addChild(this.dynamic);
    this.chars.focus = Math.max(
      0, CHARACTER_LIST.findIndex((c) => c.id === ctx.profile.lastCharacter),
    );
  }

  enter(): void {}
  exit(): void {}

  /** Re-fit on any screen-size change (also covers the first frame). */
  private syncLayout(): void {
    const { width, height } = this.ctx.app.screen;
    if (width === this.lastW && height === this.lastH) return;
    this.lastW = width;
    this.lastH = height;
    const { w, h } = fitScene(this.root, width, height, MIN_W, MIN_H);
    this.vw = w;
    this.vh = h;
    this.gridY = 96;
    this.panelY = this.gridY + 2 * CARD_H + CARD_GAP + 12; // grid ends at 366
    this.tierY = this.panelY + PANEL_H + 12;
    this.continueY = this.tierY + 48;
    this.dirty = true;
  }

  update(_dt: number, input: InputSnapshot): void {
    this.syncLayout();
    if (input.device !== this.lastDevice) {
      this.lastDevice = input.device;
      this.dirty = true;
    }
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }

    if (input.navY !== 0) {
      if (this.row === 0) {
        // Grid-aware vertical nav: move within the 5+4 card grid first, then
        // fall through to the tier row from the bottom card row.
        const focus = this.chars.focus;
        const gridRow = focus < GRID_COLS ? 0 : 1;
        const col = gridRow === 1 ? focus - GRID_COLS : focus;
        if (input.navY > 0) {
          if (gridRow === 0) {
            this.chars.focus = GRID_COLS + Math.min(col, CHARACTER_LIST.length - GRID_COLS - 1);
          } else {
            this.row = 1;
          }
          this.dirty = true;
        } else if (gridRow === 1) {
          this.chars.focus = Math.min(col, GRID_COLS - 1);
          this.dirty = true;
        } // top edge: no-op
      } else {
        const next = Math.max(0, Math.min(2, this.row + input.navY)) as Row;
        if (next !== this.row) {
          this.row = next;
          this.dirty = true;
        }
      }
    }

    if (this.row === 0) {
      if (this.chars.move(input.navX)) this.dirty = true;
      if (input.confirmPressed) {
        const def = CHARACTER_LIST[this.chars.focus]!;
        if (isCharacterUnlocked(this.ctx.profile.data, def)) {
          this.ctx.profile.setLastCharacter(def.id);
          this.dirty = true;
        }
      }
    } else if (this.row === 1) {
      const profile = this.ctx.profile;
      const maxTier = maxUnlockedTier(profile.data);
      if (input.navX !== 0 && maxTier > 0) {
        const current = Math.min(profile.data.lastTier, maxTier);
        const next = Math.max(0, Math.min(maxTier, current + input.navX));
        if (next !== current || next !== profile.data.lastTier) {
          profile.setLastTier(next);
          this.dirty = true;
        }
      }
      if (input.confirmPressed) this.continue_();
    } else if (input.confirmPressed) {
      this.continue_();
    }

    if (input.cancelPressed) this.ctx.scenes.pop();
  }

  private continue_(): void {
    this.ctx.scenes.push(new RelicScene(this.ctx, this.seed));
  }

  render(_alpha: number): void {
    this.syncLayout();
    if (!this.dirty) return;
    this.dirty = false;
    this.redraw();
  }

  private redraw(): void {
    const profile = this.ctx.profile;
    const w = this.vw;
    this.dynamic.removeChildren().forEach((c) => c.destroy({ children: true }));
    const g = new Graphics();
    this.dynamic.addChild(g);
    g.rect(0, 0, w, this.vh).fill(Palette.bg);

    this.text('THE DESCENT', w / 2, 24, {
      fontSize: 34, fontWeight: 'bold', fill: Palette.hudText, letterSpacing: 6,
    }, 0.5);
    this.text(`seed ${this.seed} — choose who goes down`, w / 2, 66, {
      fontSize: 12, fill: Palette.hudDim,
    }, 0.5);

    // Character cards: 5+4 grid, name + desc on the card (picture slot
    // reserved — the glyph stands in for art until portraits exist).
    CHARACTER_LIST.forEach((cdef, i) => {
      const gridRow = i < GRID_COLS ? 0 : 1;
      const col = gridRow === 1 ? i - GRID_COLS : i;
      const rowCount = gridRow === 0 ? GRID_COLS : CHARACTER_LIST.length - GRID_COLS;
      const rowW = rowCount * CARD_W + (rowCount - 1) * CARD_GAP;
      const x = (w - rowW) / 2 + col * (CARD_W + CARD_GAP);
      const y = this.gridY + gridRow * (CARD_H + CARD_GAP);
      const unlocked = isCharacterUnlocked(profile.data, cdef);
      const selected = profile.lastCharacter === cdef.id;
      const focused = this.row === 0 && i === this.chars.focus;

      g.roundRect(x, y, CARD_W, CARD_H, 8)
        .fill({ color: Palette.hudPanel, alpha: 0.9 })
        .stroke({
          width: selected || focused ? 2.5 : 1.5,
          color: focused ? Palette.focus : selected ? Palette.playerCool : Palette.hudDim,
          alpha: unlocked ? 0.9 : 0.35,
        });
      if (selected) g.rect(x + 10, y + CARD_H - 4, CARD_W - 20, 2.5).fill(Palette.playerCool);

      // Picture placeholder (portrait slot) with the glyph as stand-in art.
      g.roundRect(x + 8, y + 8, CARD_W - 16, 40, 4)
        .stroke({ width: 1, color: Palette.hudDim, alpha: 0.3 });
      const glyph = this.text(cdef.short, x + CARD_W / 2, y + 28, {
        fontSize: 20, fontWeight: 'bold',
        fill: !unlocked ? Palette.hudDim : selected ? Palette.playerCool : Palette.hudText,
      }, 0.5, 0.5);
      if (!unlocked) {
        glyph.alpha = 0.25;
        const lx = x + CARD_W - 22;
        const ly = y + 30;
        g.rect(lx - 5, ly - 2, 10, 7).fill({ color: Palette.hudDim, alpha: 0.9 });
        g.circle(lx, ly - 3, 3.5).stroke({ width: 1.5, color: Palette.hudDim, alpha: 0.9 });
      }

      // "Hale, the Steady" → name line + dim epithet line (full names
      // overflow a 172px card).
      const comma = cdef.name.indexOf(', ');
      const shortName = comma >= 0 ? cdef.name.slice(0, comma) : cdef.name;
      const epithet = comma >= 0 ? cdef.name.slice(comma + 2) : '';
      this.text(
        `${shortName}${selected ? ' ◈' : ''}`,
        x + 10, y + 52,
        {
          fontSize: 13, fontWeight: 'bold',
          fill: !unlocked ? Palette.hudDim : selected ? Palette.playerCool : Palette.hudText,
        },
      );
      if (epithet) {
        this.text(epithet, x + 10, y + 68, {
          fontSize: 9, fill: unlocked ? Palette.gold : Palette.hudDim,
        });
      }
      const body = unlocked ? cdef.desc : `Unlock: ${cdef.unlockHint ?? '???'}`;
      const bodyText = this.text(body, x + 10, y + 82, {
        fontSize: 9, fill: unlocked ? Palette.hudText : Palette.hudDim,
        wordWrap: true, wordWrapWidth: CARD_W - 20,
      });
      if (!unlocked) bodyText.alpha = 0.8;
    });

    // Compact detail strip — the focused card (else the selected character):
    // the lines that don't fit on a card (signature / aspect / active).
    const shown = this.row === 0
      ? CHARACTER_LIST[this.chars.focus]!
      : CHARACTER_LIST.find((c) => c.id === profile.lastCharacter) ?? CHARACTER_LIST[0]!;
    const unlocked = isCharacterUnlocked(profile.data, shown);
    const px = (w - PANEL_W) / 2;
    g.roundRect(px, this.panelY, PANEL_W, PANEL_H, 8)
      .fill({ color: Palette.hudPanel, alpha: 0.7 })
      .stroke({ width: 1.5, color: Palette.hudDim, alpha: 0.4 });

    const sig = ABILITY_DEFS[shown.signatureAbility];
    this.text(`signature — ${sig.name}`, px + 18, this.panelY + 8, {
      fontSize: 12, fill: Palette.hudDim,
    });
    if (unlocked && shown.aspectName !== null) {
      this.text(`${shown.aspectName} — ${shown.aspectBlurb ?? ''}`, px + 18, this.panelY + 27, {
        fontSize: 12, fill: Palette.gold,
      });
    } else if (!unlocked) {
      this.text(`Unlock: ${shown.unlockHint ?? '???'}`, px + 18, this.panelY + 27, {
        fontSize: 12, fill: Palette.hudDim,
      });
    } else {
      this.text('No aspect — the steady baseline. What you see is what he is.',
        px + 18, this.panelY + 27, { fontSize: 12, fill: Palette.gold });
    }
    if (unlocked) {
      const act = ACTIVE_DEFS[shown.id];
      this.text(`active — ${act.name}: ${act.blurb}`, px + 18, this.panelY + 46, {
        fontSize: 12, fill: Palette.playerCoolSoft,
      });
    }

    // Tier ladder (identical semantics to the old loadout row).
    const maxTier = maxUnlockedTier(profile.data);
    const pickedTier = Math.min(profile.data.lastTier, maxTier);
    const tierFocused = this.row === 1;
    let tierMain: string;
    let tierSub: string;
    if (maxTier === 0) {
      tierMain = 'THE DESCENT';
      tierSub = `win a run to unlock ${tierLabel(1)}`;
    } else {
      const name = pickedTier === 0 ? 'THE DESCENT' : tierLabel(pickedTier).toUpperCase();
      tierMain = `${pickedTier > 0 ? '◂  ' : '   '}${name}${pickedTier < maxTier ? '  ▸' : '   '}`;
      if (pickedTier === 0) {
        tierSub = 'the base game — no scaling';
      } else {
        const hp = Math.round((tierHpMult(pickedTier) - 1) * 100);
        const dmg = Math.round((tierDamageMult(pickedTier) - 1) * 100);
        const qf = Math.round(tierQualityFloor(pickedTier) * 100);
        const best = bestTime(profile.data, pickedTier);
        tierSub = `foes +${hp}% hp, +${dmg}% dmg  ·  loot floor ${qf}%` +
          (best !== undefined ? `  ·  best ${formatRunTime(best)}` : '');
      }
      if (pickedTier === maxTier && pickedTier > 0) {
        tierSub += '  ·  win to unlock the next';
      }
    }
    this.text((tierFocused ? '▸ ' : '  ') + tierMain, w / 2, this.tierY, {
      fontSize: 15, fontWeight: 'bold', letterSpacing: 2,
      fill: maxTier === 0 ? Palette.hudDim : tierFocused ? Palette.focus : Palette.hudText,
    }, 0.5);
    this.text(tierSub, w / 2, this.tierY + 22, { fontSize: 11, fill: Palette.hudDim }, 0.5);

    // Continue.
    const tierSuffix = pickedTier > 0 ? ` — ${tierLabel(pickedTier)}` : '';
    this.text(
      (this.row === 2 ? '▸ ' : '  ') + `CONTINUE — choose relics${tierSuffix}`,
      w / 2, this.continueY,
      {
        fontSize: 18, fontWeight: 'bold', letterSpacing: 2,
        fill: this.row === 2 ? Palette.gold : Palette.hudText,
      }, 0.5,
    );

    const hint = this.lastDevice === 'pad'
      ? 'navigate  [dpad]     select  [Ⓐ]     back  [Ⓑ]'
      : 'navigate  [↑↓←→]     select  [E]     back  [Esc]';
    this.text(hint, w / 2, this.vh - 30, { fontSize: 12, fill: Palette.hudDim }, 0.5);
  }

  private text(
    content: string, x: number, y: number,
    style: TextStyleOptions,
    anchorX = 0, anchorY = 0,
  ): Text {
    const t = new Text({ text: content, style: { fontFamily: 'monospace', ...style } });
    t.anchor.set(anchorX, anchorY);
    t.position.set(x, y);
    this.dynamic.addChild(t);
    return t;
  }
}
